import React, { useEffect, useMemo, useState } from 'react';
import { Typography } from 'antd';
import { FabricObject } from 'fabric';
import { ChevronDown, ChevronRight, Film, Type } from 'lucide-react';
import { colors, dimensions, TimelineMode, typography } from '../../../theme';
import { buildAttachmentGraph, getConnectedClipIds, TimelineClipEntry } from '../../utils/attachment-graph';
import { filterGroupableEntries, LaneRegistry, partitionTimelineRows } from '../../utils/timeline-rows';
import { LayerRow } from './layer-row';

const { Text } = Typography;
const HEADER_HEIGHT = 28;

export type LayerEntry = TimelineClipEntry;

// Solo el grupo de video es desplegable: el de texto siempre muestra el conteo, sin contenido.
type GroupKind = 'video';

type LayerDisplayRow =
  | { kind: 'video-group'; trackNumber: number; entries: LayerEntry[] }
  | { kind: 'text-group'; trackNumber: number; entries: LayerEntry[] }
  | { kind: 'solo'; trackNumber: number; entries: LayerEntry[] };

type LayerColumnProps = {
  entries: LayerEntry[];
  // Mapa clip id -> lane, ver comentario en timeline-rows.ts. Compartido con TrackArea para que el
  // agrupamiento de filas coincida siempre, fila a fila, ya que ambas columnas se muestran lado a
  // lado con scroll sincronizado.
  laneRegistry: LaneRegistry;
  selectedObject: FabricObject | null;
  mode: TimelineMode;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  onScroll: () => void;
  onSelectLayer: (entry: LayerEntry) => void;
  onToggleVisibility: (entry: LayerEntry) => void;
  onToggleLock: (entry: LayerEntry) => void;
  onUngroup?: () => void;
  onDuplicate: (entry: LayerEntry) => void;
  onDelete: (entry: LayerEntry) => void;

  canMoveUp: (entry: LayerEntry) => boolean;
  canMoveDown: (entry: LayerEntry) => boolean;
  onBringForward: (entry: LayerEntry) => void;
  onSendBackward: (entry: LayerEntry) => void;
};

export const LayerColumn: React.FC<LayerColumnProps> = ({
  entries,
  laneRegistry,
  selectedObject,
  mode,
  scrollRef,
  onScroll,
  onSelectLayer,
  onToggleVisibility,
  onToggleLock,
  onUngroup,
  onDuplicate,
  onDelete,
  canMoveUp,
  canMoveDown,
  onBringForward,
  onSendBackward
}) => {
  const td = dimensions.timeline[mode];
  const [collapsedGroups, setCollapsedGroups] = useState<Set<GroupKind>>(new Set());

  // Igual que en TrackArea: el attachment graph solo se arma con los clips del grupo compartido
  // de video/texto, para que un track solo (nuevo o duplicado) nunca quede enganchado a otro por
  // coincidir en el tiempo.
  const groupableEntries = useMemo(() => filterGroupableEntries(entries, laneRegistry), [entries, laneRegistry]);
  const attachmentGraph = useMemo(() => buildAttachmentGraph(groupableEntries), [groupableEntries]);
  const selectedClipIds = useMemo(
    () => (selectedObject?.layerId ? getConnectedClipIds(attachmentGraph, selectedObject.layerId) : null),
    [attachmentGraph, selectedObject]
  );

  const rows = useMemo<LayerDisplayRow[]>(
    () =>
      partitionTimelineRows(entries, laneRegistry).map((bucket, index) => {
        const trackNumber = index + 1;
        return { kind: bucket.kind, trackNumber, entries: bucket.entries };
      }),
    [entries, laneRegistry]
  );

  // Si la selección cae dentro del track de video colapsado, lo despliega.
  useEffect(() => {
    if (!selectedClipIds || !collapsedGroups.has('video')) return;
    const videoRow = rows.find((row): row is Extract<LayerDisplayRow, { kind: 'video-group' }> => row.kind === 'video-group');
    if (!videoRow) return;
    const hasSelection = videoRow.entries.some(entry => !!entry.clip && selectedClipIds.has(entry.clip.id));
    if (!hasSelection) return;
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      next.delete('video');
      return next;
    });
  }, [selectedClipIds, rows, collapsedGroups]);

  const toggleGroup = (groupKind: GroupKind) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKind)) next.delete(groupKind);
      else next.add(groupKind);
      return next;
    });
  };

  const renderGroupHeader = (opts: {
    icon: React.ReactNode;
    label: string;
    count: number;
    isExpanded: boolean;
    isSelected: boolean;
    onToggle: () => void;
  }) => (
    <div
      onClick={opts.onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        height: td.rowHeight,
        minHeight: td.rowHeight,
        flexShrink: 0,
        gap: dimensions.spacing.lg,
        padding: `0 ${dimensions.spacing.xl}px`,
        backgroundColor: opts.isSelected ? colors.rowSelected : colors.rowBg,
        borderLeft: `2px solid ${opts.isSelected ? colors.accent : 'transparent'}`,
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'background-color 0.12s, height 0.2s ease, min-height 0.2s ease'
      }}
    >
      <span style={{ color: colors.textSecondary, display: 'flex', flexShrink: 0 }}>
        {opts.isExpanded ? <ChevronDown size={dimensions.size.iconSm} /> : <ChevronRight size={dimensions.size.iconSm} />}
      </span>
      <span style={{ color: colors.textSecondary, display: 'flex', flexShrink: 0 }}>{opts.icon}</span>
      <span
        style={{
          flex: 1,
          fontSize: typography.fontSize.base,
          fontFamily: typography.fontFamily.ui,
          color: colors.textPrimary,
          textTransform: 'none'
        }}
      >
        {opts.label}
      </span>
      <span style={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>{opts.count}</span>
    </div>
  );

  const renderVideoGroupRow = (row: Extract<LayerDisplayRow, { kind: 'video-group' }>) => {
    const isExpanded = !collapsedGroups.has('video');
    const isSelected =
      !!selectedClipIds && row.entries.some(entry => !!entry.clip && selectedClipIds.has(entry.clip.id));

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: td.rowGap }}>
        {renderGroupHeader({
          icon: <Film size={dimensions.size.iconSm} />,
          label: `Track ${row.trackNumber}`,
          count: row.entries.length,
          isExpanded,
          isSelected,
          onToggle: () => toggleGroup('video')
        })}

        {isExpanded && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: td.rowGap, marginLeft: dimensions.spacing.xl }}>
            {row.entries.map(entry => (
              <LayerRow
                key={entry.track.id}
                track={entry.track}
                mode={mode}
                isSelected={!!selectedClipIds && !!entry.clip && selectedClipIds.has(entry.clip.id)}
                isGroup={entry.fabricObject?.type === 'group'}
                onSelect={() => onSelectLayer(entry)}
                onToggleVisibility={() => onToggleVisibility(entry)}
                onToggleLock={() => onToggleLock(entry)}
                onUngroup={onUngroup}
                onDuplicate={() => onDuplicate(entry)}
                onDelete={() => onDelete(entry)}
                canMoveUp={canMoveUp(entry)}
                canMoveDown={canMoveDown(entry)}
                onBringForward={() => onBringForward(entry)}
                onSendBackward={() => onSendBackward(entry)}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTextGroupRow = (row: Extract<LayerDisplayRow, { kind: 'text-group' }>) => {
    const isSelected =
      !!selectedClipIds && row.entries.some(entry => !!entry.clip && selectedClipIds.has(entry.clip.id));

    // No es desplegable: no hay contenido individual que mostrar, solo la cantidad de subtítulos.
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          height: td.rowHeight,
          minHeight: td.rowHeight,
          flexShrink: 0,
          gap: dimensions.spacing.lg,
          padding: `0 ${dimensions.spacing.xl}px`,
          backgroundColor: isSelected ? colors.rowSelected : colors.rowBg,
          borderLeft: `2px solid ${isSelected ? colors.accent : 'transparent'}`,
          userSelect: 'none'
        }}
      >
        <span style={{ color: colors.textSecondary, display: 'flex', flexShrink: 0 }}>
          <Type size={dimensions.size.iconSm} />
        </span>
        <span
          style={{
            flex: 1,
            fontSize: typography.fontSize.base,
            fontFamily: typography.fontFamily.ui,
            color: colors.textPrimary,
            textTransform: 'none'
          }}
        >
          {`Track ${row.trackNumber}`}
        </span>
        <span style={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>
          {`Subtitulos (${row.entries.length})`}
        </span>
      </div>
    );
  };

  // Una lane propia: un track nuevo, o uno con sus duplicados (todos listados en la misma fila).
  const renderSoloRow = (row: Extract<LayerDisplayRow, { kind: 'solo' }>) => {
    const first = row.entries[0];

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          onClick={() => onSelectLayer(first)}
          style={{
            padding: `0 ${dimensions.spacing.xl}px`,
            fontSize: typography.fontSize.base,
            fontFamily: typography.fontFamily.ui,
            color: colors.textPrimary,
            textTransform: 'none',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          {`Track ${row.trackNumber}`}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: td.rowGap }}>
          {row.entries.map(entry => (
            <LayerRow
              key={entry.track.id}
              track={entry.track}
              mode={mode}
              isSelected={!!selectedClipIds && !!entry.clip && selectedClipIds.has(entry.clip.id)}
              isGroup={entry.fabricObject?.type === 'group'}
              onSelect={() => onSelectLayer(entry)}
              onToggleVisibility={() => onToggleVisibility(entry)}
              onToggleLock={() => onToggleLock(entry)}
              onUngroup={onUngroup}
              onDuplicate={() => onDuplicate(entry)}
              onDelete={() => onDelete(entry)}
              canMoveUp={canMoveUp(entry)}
              canMoveDown={canMoveDown(entry)}
              onBringForward={() => onBringForward(entry)}
              onSendBackward={() => onSendBackward(entry)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        width: td.layerColumnWidth,
        minWidth: td.layerColumnWidth,
        flexShrink: 0,
        backgroundColor: colors.sidebarBg,
        borderRight: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div
        style={{
          height: HEADER_HEIGHT,
          minHeight: HEADER_HEIGHT,
          padding: `0 ${dimensions.spacing.xl}px`,
          display: 'flex',
          alignItems: 'center',
          borderBottom: `1px solid ${colors.border}`
        }}
      >
        <Text
          strong
          style={{
            fontFamily: typography.fontFamily.ui,
            fontSize: typography.fontSize.sm,
            color: colors.textMuted,
            letterSpacing: typography.letterSpacing.wider,
            textTransform: 'uppercase'
          }}
        >
          Capas
        </Text>
      </div>

      <div
        ref={scrollRef}
        onScroll={onScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gap: td.rowGap,
          padding: `${td.rowGap}px 0`,
          textAlign: 'left'
        }}
      >
        {rows.map(row => {
          if (row.kind === 'video-group') return <React.Fragment key='video-group'>{renderVideoGroupRow(row)}</React.Fragment>;
          if (row.kind === 'text-group') return <React.Fragment key='text-group'>{renderTextGroupRow(row)}</React.Fragment>;
          return <React.Fragment key={row.entries[0].track.id}>{renderSoloRow(row)}</React.Fragment>;
        })}
      </div>
    </div>
  );
};
